import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, 
  Phone, 
  FileText, 
  PenTool, 
  Send, 
  CheckCircle2, 
  Star,
  Shield,
  Heart,
  Users
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CEO_PHOTO = "https://customer-assets.emergentagent.com/job_earnest-refunds/artifacts/wnjy4cuo_ChatGPT%20Image%20Feb%2017%2C%202026%2C%2012_29_05%20PM.png";
const HERO_BG = "https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1973&q=80";

// Success Stories Images
const successStories = [
  {
    image: "https://customer-assets.emergentagent.com/job_e1b79d0e-9ea4-4f7b-8d98-a9785f01758d/artifacts/lt3r4akk_ChatGPT%20Image%20Mar%202%2C%202026%2C%2002_11_33%20PM.png",
    amount: "$161,277",
    description: "Recovered for a couple in Arizona"
  },
  {
    image: "https://customer-assets.emergentagent.com/job_e1b79d0e-9ea4-4f7b-8d98-a9785f01758d/artifacts/3u6s9yej_ChatGPT%20Image%20Mar%202%2C%202026%2C%2002_33_23%20PM.png",
    amount: "$144,712",
    description: "Recovered for a family in California"
  },
  {
    image: "https://customer-assets.emergentagent.com/job_e1b79d0e-9ea4-4f7b-8d98-a9785f01758d/artifacts/339e4ewf_ChatGPT%20Image%20Mar%202%2C%202026%2C%2002_20_08%20PM.png",
    amount: "$216,442",
    description: "Another happy family helped"
  }
];

const TEAM_PHOTO = "https://customer-assets.emergentagent.com/job_e1b79d0e-9ea4-4f7b-8d98-a9785f01758d/artifacts/tple5nn3_ChatGPT%20Image%20Mar%202%2C%202026%2C%2002_18_19%20PM.png";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const processSteps = [
  {
    icon: Phone,
    title: "Contact Us",
    description: "Reach out by phone or through our contact form. We'll discuss your situation and determine if you have overage funds waiting."
  },
  {
    icon: FileText,
    title: "Receive Paperwork",
    description: "We'll send you all the necessary documents needed to recover your funds, typically within the same day."
  },
  {
    icon: PenTool,
    title: "Sign & Notarize",
    description: "Review and sign the paperwork, then have it notarized. We'll guide you through every step."
  },
  {
    icon: Send,
    title: "Return Documents",
    description: "Send the completed paperwork back to us via mail or email. We handle everything from there."
  },
  {
    icon: CheckCircle2,
    title: "We Handle the Rest",
    description: "Sit back while we work with the county and attorneys. We'll keep you updated on your claim status."
  }
];

const values = [
  {
    icon: Shield,
    title: "Integrity",
    description: "We operate with complete honesty and transparency. No hidden fees, no surprises."
  },
  {
    icon: Users,
    title: "Reliance",
    description: "You can count on us. We're with you every step of the way until your funds are recovered."
  },
  {
    icon: Heart,
    title: "Compassion",
    description: "We understand what you've been through. We treat every client like family."
  }
];

export default function HomePage() {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
        setTestimonials(response.data);
      } catch (error) {
        console.error("Error fetching testimonials:", error);
      }
    };
    fetchTestimonials();
  }, []);

  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center hero-gradient overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-10"
            style={{ backgroundImage: `url(${HERO_BG})` }}
          />
        </div>
        
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-block text-emerald-600 font-medium mb-4 uppercase tracking-wider text-sm" data-testid="hero-subtitle">
                Overage Refund Services
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6" data-testid="hero-title">
                Recover the Money <span className="text-emerald-600">You're Owed</span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-xl">
                After a foreclosure, you may have surplus funds waiting for you. 
                Cornerstone Refund helps California and Arizona property owners 
                claim their money — with no upfront costs.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link to="/contact">
                  <Button className="btn-primary w-full sm:w-auto" data-testid="hero-get-started">
                    Get Started Today
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="tel:405-754-2183">
                  <Button variant="outline" className="btn-secondary w-full sm:w-auto" data-testid="hero-call-now">
                    <Phone className="mr-2 w-5 h-5" />
                    Call 405-754-2183
                  </Button>
                </a>
              </div>

              {/* Trust indicators */}
              <div className="flex items-center gap-6 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  <span>No Upfront Fees</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  <span>Free Consultation</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:block"
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-emerald-100 rounded-3xl transform rotate-3"></div>
                <img 
                  src={CEO_PHOTO}
                  alt="Michael Ernest, CEO" 
                  className="relative rounded-2xl shadow-2xl w-full max-w-md mx-auto object-cover"
                  data-testid="hero-ceo-photo"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 max-w-xs">
                  <p className="text-slate-700 font-medium">Michael Ernest</p>
                  <p className="text-slate-500 text-sm">CEO, Cornerstone Refund</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.span variants={fadeInUp} className="text-emerald-600 font-medium uppercase tracking-wider text-sm">
              Our Foundation
            </motion.span>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-slate-900 mt-2">
              Built on Trust
            </motion.h2>
          </motion.div>

          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {values.map((value, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card className="card-hover border-slate-100 h-full" data-testid={`value-card-${index}`}>
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <value.icon className="w-8 h-8 text-emerald-600" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-3">{value.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.span variants={fadeInUp} className="text-emerald-600 font-medium uppercase tracking-wider text-sm">
              Our Services
            </motion.span>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-slate-900 mt-2">
              Serving California, Arizona & Georgia
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-slate-600 mt-4 max-w-2xl mx-auto">
              We specialize in overage refund recovery in multiple states, with dedicated teams familiar with each state's unique requirements.
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Link to="/california">
                <Card className="card-hover border-slate-100 overflow-hidden group h-full" data-testid="service-california">
                  <CardContent className="p-0">
                    <div className="h-40 bg-gradient-to-br from-amber-500 to-orange-600 relative overflow-hidden">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-7xl font-bold text-white/20">CA</span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-semibold text-slate-900 mb-2 group-hover:text-emerald-600 transition-colors">
                        California
                      </h3>
                      <p className="text-slate-600 text-sm mb-3">
                        Navigate California's county systems to recover your surplus funds.
                      </p>
                      <span className="inline-flex items-center text-emerald-600 font-medium text-sm">
                        Learn More <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <Link to="/arizona">
                <Card className="card-hover border-slate-100 overflow-hidden group h-full" data-testid="service-arizona">
                  <CardContent className="p-0">
                    <div className="h-40 bg-gradient-to-br from-red-500 to-rose-600 relative overflow-hidden">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-7xl font-bold text-white/20">AZ</span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-semibold text-slate-900 mb-2 group-hover:text-emerald-600 transition-colors">
                        Arizona
                      </h3>
                      <p className="text-slate-600 text-sm mb-3">
                        Expert knowledge of Arizona's procedures for fund recovery.
                      </p>
                      <span className="inline-flex items-center text-emerald-600 font-medium text-sm">
                        Learn More <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Link to="/georgia">
                <Card className="card-hover border-slate-100 overflow-hidden group h-full" data-testid="service-georgia">
                  <CardContent className="p-0">
                    <div className="h-40 bg-gradient-to-br from-rose-500 to-pink-600 relative overflow-hidden">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-7xl font-bold text-white/20">GA</span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-semibold text-slate-900 mb-2 group-hover:text-emerald-600 transition-colors">
                        Georgia
                      </h3>
                      <p className="text-slate-600 text-sm mb-3">
                        Specialized assistance for Georgia surplus fund recovery.
                      </p>
                      <span className="inline-flex items-center text-emerald-600 font-medium text-sm">
                        Learn More <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span variants={fadeInUp} className="text-emerald-600 font-medium uppercase tracking-wider text-sm">
              Simple Process
            </motion.span>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-slate-900 mt-2">
              How It Works
            </motion.h2>
          </motion.div>

          <div className="max-w-3xl mx-auto">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="process-step flex gap-6 pb-12"
                data-testid={`process-step-${index}`}
              >
                <div className="relative z-10">
                  <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center text-white font-semibold shadow-lg">
                    {index + 1}
                  </div>
                </div>
                <div className="flex-1 pt-1">
                  <h3 className="text-xl font-semibold text-slate-900 mb-2 flex items-center gap-3">
                    <step.icon className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
                    {step.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/faqs">
              <Button variant="outline" className="btn-secondary" data-testid="view-faqs-btn">
                View FAQs
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Success Stories Section */}
      <section className="py-16 md:py-24 bg-emerald-600">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.span variants={fadeInUp} className="text-emerald-100 font-medium uppercase tracking-wider text-sm">
              Real Results
            </motion.span>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-white mt-2">
              Success Stories
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-emerald-100 mt-4 max-w-2xl mx-auto">
              See how we've helped families recover their overage funds. These are real clients with real results.
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {successStories.map((story, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="group"
              >
                <div className="bg-white rounded-2xl overflow-hidden shadow-xl transform transition-transform duration-300 group-hover:scale-105" data-testid={`success-story-${index}`}>
                  <div className="aspect-square overflow-hidden">
                    <img 
                      src={story.image} 
                      alt={`Client success - ${story.amount} recovered`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6 text-center">
                    <p className="text-3xl font-bold text-emerald-600 mb-1">{story.amount}</p>
                    <p className="text-slate-600">{story.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-center mt-12"
          >
            <Link to="/contact">
              <Button className="bg-white text-emerald-600 hover:bg-emerald-50 px-8 py-3 text-lg font-semibold" data-testid="success-cta-btn">
                Start Your Recovery Today
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.span variants={fadeInUp} className="text-emerald-600 font-medium uppercase tracking-wider text-sm">
              Client Stories
            </motion.span>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-slate-900 mt-2">
              What Our Clients Say
            </motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.slice(0, 3).map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="testimonial-card h-full border-0" data-testid={`testimonial-${index}`}>
                  <CardContent className="p-8 pt-12">
                    <div className="flex gap-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i} 
                          className={`w-5 h-5 ${i < testimonial.rating ? 'star-filled fill-current' : 'star-empty'}`} 
                        />
                      ))}
                    </div>
                    <p className="text-slate-700 leading-relaxed mb-6">
                      {testimonial.text}
                    </p>
                    <div className="border-t border-slate-200 pt-4">
                      <p className="font-semibold text-slate-900">{testimonial.name}</p>
                      <p className="text-slate-500 text-sm">{testimonial.location}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-slate-900">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Recover Your Funds?
            </h2>
            <p className="text-slate-400 text-lg mb-8 max-w-2xl mx-auto">
              Contact us today for a free consultation. No upfront fees, no obligation — just honest guidance from people who care.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-lg" data-testid="cta-contact-btn">
                  Get Started Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:405-754-2183">
                <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800 px-8 py-6 text-lg" data-testid="cta-call-btn">
                  <Phone className="mr-2 w-5 h-5" />
                  405-754-2183
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
